import { ChevronDown, ChevronUp, Copy03, Cube01, Plus, Trash01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_LINE_ITEMS } from 'src/app/MyQuote/AddQuote/Steps/Dimensions/LineItem';
import VisualizeUnit from 'src/app/MyQuote/Modals/VisualizeUnit';
import InputText from 'src/components/InputText/InputText';
import Radio from 'src/components/Radio/Radio';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import TooltipCmp from 'src/components/TooltipCmp';
import { useAppSelector } from 'src/redux/hooks';
import { selectCurrentLegData } from 'src/redux/orders.slice';
import { getCalculatedFreightClass, getStackingBlockedMessage, isStackingBlocked } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

export interface ILineItem {
    id?: number | null;
    handlingUnit: number | null;
    handlingUnitNo: number;
    weight: number;
    itemLength: number;
    width: number;
    height: number;
    totalWeight: number;
    freightClass: number;
    commodityId: string | null;
    commodityName: string | null;
    isStack: boolean;
    weightMeasurement: string;
}

interface LineItemsSectionProps {
    lineItems: ILineItem[];
    onLineItemsChange: (items: ILineItem[]) => void;
    errors: any;
    isLoading: boolean;
}

const INITIAL_LINE_ITEM: ILineItem = {
    id: null,
    handlingUnit: 1,
    handlingUnitNo: 0,
    weight: 0,
    itemLength: 0,
    width: 0,
    height: 0,
    totalWeight: 0,
    freightClass: 0,
    commodityId: null,
    commodityName: null,
    isStack: true,
    weightMeasurement: 'lbs',
};

const LineItemsSection = ({ lineItems, onLineItemsChange, errors, isLoading }: LineItemsSectionProps) => {
    const { t } = useTranslation();
    const { handlingUnitOptions, commodityOptions } = useAppSelector((state) => state.basic);
    const currentLegData = useAppSelector(selectCurrentLegData);
    const [weightUnit, setWeightUnit] = useState<'lbs' | 'kgs'>('lbs');
    const [openItems, setOpenItems] = useState<Set<number>>(new Set([0]));
    const [visualizeIndex, setVisualizeIndex] = useState<number | null>(null);

    // Length unit based on weight unit: lbs -> in, kgs -> cm
    const lengthUnit = weightUnit === 'lbs' ? 'in' : 'cm';

    const toggleItem = (index: number) => {
        setOpenItems((prev) => {
            const newSet = new Set(prev);

            // Allow multiple items to be expanded manually
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }

            return newSet;
        });
    };

    const handleAddLineItem = () => {
        if (lineItems.length >= MAX_LINE_ITEMS) {
            WalToast.error(t('Orders.Max_Line_Items_Reached') as string);

            return;
        }

        const newIndex = lineItems.length;
        onLineItemsChange([...lineItems, { ...INITIAL_LINE_ITEM, weightMeasurement: weightUnit }]);
        setOpenItems(new Set([newIndex]));
    };

    const handleDuplicateLineItem = (index: number) => {
        if (lineItems.length >= MAX_LINE_ITEMS) {
            WalToast.error(t('Orders.Max_Line_Items_Reached') as string);

            return;
        }

        const itemToDuplicate = { ...lineItems[index], id: null };
        const newItems = [...lineItems, itemToDuplicate];
        onLineItemsChange(newItems);
        setOpenItems(new Set([newItems.length - 1]));
    };

    const handleRemoveLineItem = (index: number) => {
        if (lineItems.length > 1) {
            const newItems = lineItems.filter((_, i) => i !== index);
            onLineItemsChange(newItems);
            setOpenItems((prev) => {
                const newSet = new Set<number>();

                prev.forEach((i) => {
                    if (i < index) newSet.add(i);
                    else if (i > index) newSet.add(i - 1);
                });

                return newSet;
            });
        } else {
            onLineItemsChange([{ ...INITIAL_LINE_ITEM, weightMeasurement: weightUnit }]);
        }
    };

    const handleLineItemChange = (index: number, field: keyof ILineItem, value: any) => {
        const newItems = [...lineItems];
        const updatedItem = { ...newItems[index], [field]: value };

        // When unit weight or quantity changes, calculate total weight
        if (field === 'weight' || field === 'handlingUnitNo') {
            updatedItem.totalWeight = updatedItem.weight * updatedItem.handlingUnitNo;
        }

        // When total weight is changed manually, calculate unit weight
        if (field === 'totalWeight' && updatedItem.handlingUnitNo > 0) {
            updatedItem.weight = updatedItem.totalWeight / updatedItem.handlingUnitNo;
        }

        // Auto-calculate freight class when dimensions or weight change
        if (field === 'weight' || field === 'itemLength' || field === 'width' || field === 'height' || field === 'totalWeight') {
            const { weight, itemLength, width, height } = updatedItem;

            if (weight && itemLength && width && height) {
                const freightData = getCalculatedFreightClass({
                    itemLength: itemLength || 0,
                    width: width || 0,
                    height: height || 0,
                    weight: weight || 0,
                    lengthUnit: lengthUnit,
                    weightUnit: weightUnit,
                });
                updatedItem.freightClass = freightData?.freightClass || 0;
            } else {
                updatedItem.freightClass = 0;
            }
        }

        newItems[index] = updatedItem;
        onLineItemsChange(newItems);
    };

    const handleWeightUnitChange = (unit: 'lbs' | 'kgs') => {
        setWeightUnit(unit);
        const updatedItems = lineItems.map((item) => ({
            ...item,
            weightMeasurement: unit,
        }));
        onLineItemsChange(updatedItems);
    };

    const handleVisualizeSubmit = (index: number, data: any) => {
        const newItems = [...lineItems];
        newItems[index] = { ...newItems[index], ...data };
        onLineItemsChange(newItems);
        setVisualizeIndex(null);
    };

    // Force isStack=true on any row whose height × 2 exceeds the bucket's stacking ceiling. Keeps state aligned with the disabled toggle so submitted data is consistent. Re-fires on measurement change because cm vs in changes the effective inches value.
    useEffect(() => {
        const wm = weightUnit.toUpperCase();
        let needsUpdate = false;
        const updatedItems = lineItems.map((item) => {
            if (isStackingBlocked(currentLegData?.equipmentType, item.height, wm) && !item.isStack) {
                needsUpdate = true;

                return { ...item, isStack: true };
            }

            return item;
        });

        if (needsUpdate) {
            onLineItemsChange(updatedItems);
        }
    }, [currentLegData?.equipmentType, weightUnit, JSON.stringify(lineItems.map((d) => ({ h: d.height, s: d.isStack })))]);

    const handleNumberChange = (index: number, field: keyof ILineItem) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
        handleLineItemChange(index, field, value);
    };

    const handleSelectChange = (index: number, field: keyof ILineItem) => (selected: any) => {
        if (field === 'handlingUnit') {
            handleLineItemChange(index, field, selected?.value || null);
        } else if (field === 'commodityId') {
            // Both fields must land in ONE update — two sequential handleLineItemChange
            // calls each rebuild from the same stale lineItems prop, so the second
            // (commodityName) would wipe the first (commodityId back to null) and the
            // dropdown selection would never stick.
            const newItems = [...lineItems];
            newItems[index] = {
                ...newItems[index],
                commodityId: selected?.value || null,
                commodityName: selected?.label || null,
            };
            onLineItemsChange(newItems);
        }
    };

    return (
        <div className="flex flex-col gap-2">
            {/* Header Row */}
            <div className="flex flex-row items-center gap-3">
                <label className="flex-1 text-xs font-medium">
                    {t('Orders.Change_Line_Items')} <span className="text-red-500">*</span>
                </label>

                <div className="flex flex-row items-center gap-1.5">
                    <Radio
                        inputName="weightUnit"
                        labelName={t('Common.Imperial_lbs')}
                        value="lbs"
                        checked={weightUnit === 'lbs'}
                        onChangeFunc={() => handleWeightUnitChange('lbs')}
                        disabled={isLoading}
                        radioLabelClassName="normal-case"
                    />
                    <Radio
                        inputName="weightUnit"
                        labelName={t('Common.Metric_kgs')}
                        value="kgs"
                        checked={weightUnit === 'kgs'}
                        onChangeFunc={() => handleWeightUnitChange('kgs')}
                        disabled={isLoading}
                        radioLabelClassName="normal-case"
                    />
                    {/* Vertical Divider */}
                    <div className="w-[1px] h-[18px] bg-[#D0D5DD]" />
                    {/* Add Line Item Button */}
                    <button type="button" onClick={handleAddLineItem} disabled={isLoading} className="flex flex-row items-center gap-1 text-[#444CE7] hover:opacity-80 transition-opacity">
                        <Plus className="w-4 h-4" />
                        <span className="text-xs font-semibold">{t('Orders.Add_Line_Item')}</span>
                    </button>
                </div>
            </div>

            {/* Line Items Container */}
            <div className="flex flex-col gap-3">
                {lineItems.map((item, index) => {
                    const isOpen = openItems.has(index);
                    const selectedHandlingUnit = handlingUnitOptions?.find((opt: any) => opt.value === item.handlingUnit) || null;
                    const selectedCommodity = commodityOptions?.find((opt: any) => opt.value === item.commodityId) || null;
                    const itemErrors = errors?.[index] || {};

                    return (
                        <div key={index} className="flex flex-col p-3 bg-[#FAFAFA] border border-[#EAECF0] rounded-lg">
                            {/* Line Item Header */}
                            <div className="flex flex-row items-center gap-1 pb-2 border-b border-[#EAECF0]">
                                <div className="flex flex-row items-center gap-1 flex-1">
                                    <span className="text-xs font-medium text-[#101828]">
                                        {t('Orders.Line_Item')} {index + 1}
                                    </span>
                                </div>
                                <div className="flex flex-row items-center gap-x-2">
                                    {/* Duplicate */}
                                    <TooltipCmp message={t('Common.Duplicate')} position="top">
                                        <button type="button" onClick={() => handleDuplicateLineItem(index)} disabled={isLoading} className="transition-colors">
                                            <Copy03 className="w-[16px] h-[16px]" />
                                        </button>
                                    </TooltipCmp>
                                    {/* Visualize */}
                                    <TooltipCmp message={t('QuotingHub.QuotingDashboard.Visualize_Unit')} position="top">
                                        <button type="button" onClick={() => setVisualizeIndex(index)} disabled={isLoading} className="transition-colors">
                                            <Cube01 className="w-[16px] h-[16px]" />
                                        </button>
                                    </TooltipCmp>
                                    {/* Delete */}
                                    <TooltipCmp message={t('Common.Remove')} position="top">
                                        <button type="button" onClick={() => handleRemoveLineItem(index)} disabled={isLoading} className="transition-colors">
                                            <Trash01 className="w-4 h-4" />
                                        </button>
                                    </TooltipCmp>
                                    {/* Expand/Collapse */}
                                    <TooltipCmp message={isOpen ? 'Collapse' : 'Expand'}>
                                        <button type="button" onClick={() => toggleItem(index)} className="rounded transition-colors">
                                            {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                        </button>
                                    </TooltipCmp>
                                </div>
                            </div>

                            {/* Always Visible Fields (First 3) */}
                            <div className="flex flex-row flex-wrap mt-3 items-start content-start gap-y-3 gap-x-3">
                                {/* Handling Unit */}
                                <div className="w-[202px]">
                                    <SelectBox
                                        label={t('Orders.Handling_Unit')}
                                        labelClassName="text-xs font-medium mb-1.5 block"
                                        placeholder={t('Orders.Select_unit')}
                                        options={handlingUnitOptions || []}
                                        value={selectedHandlingUnit}
                                        onChangeFunc={handleSelectChange(index, 'handlingUnit')}
                                        isDisabled={isLoading}
                                        required
                                        errorText={itemErrors?.handlingUnit ? true : ''}
                                    />
                                </div>

                                {/* Number of Units */}
                                <div className="w-[202px]">
                                    <InputText
                                        label={t('Orders.Number_of_Units')}
                                        labelClassName="text-xs font-medium"
                                        inputName={`handlingUnitNo_${index}`}
                                        inputType="number"
                                        placeholder="1"
                                        value={item.handlingUnitNo || ''}
                                        onChangeFunc={handleNumberChange(index, 'handlingUnitNo')}
                                        disabled={isLoading}
                                        maxLength={3}
                                        required
                                        errorText={itemErrors?.handlingUnitNo ? true : ''}
                                        min="1"
                                    />
                                </div>

                                {/* Total Weight */}
                                <div className="w-[202px]">
                                    <InputText
                                        label={`${t('Orders.Total_Weight')} (${weightUnit})`}
                                        labelClassName="text-xs font-medium"
                                        inputName={`totalWeight_${index}`}
                                        inputType="number"
                                        placeholder="0"
                                        value={item.totalWeight || ''}
                                        onChangeFunc={handleNumberChange(index, 'totalWeight')}
                                        disabled={isLoading}
                                        required
                                        errorText=""
                                        min="0"
                                        inputValueUnitType={weightUnit}
                                        shouldFormatNumber={true}
                                        maxLength={7}
                                    />
                                </div>
                            </div>

                            {/* Collapsible Fields with Animation */}
                            <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'}`}>
                                <div className="overflow-hidden flex flex-row flex-wrap items-start content-start gap-y-3 gap-x-3">
                                    {/* Length */}
                                    <div className="w-[202px]">
                                        <InputText
                                            label={`${t('Orders.Length')} (${lengthUnit})`}
                                            labelClassName="text-xs font-medium"
                                            inputName={`length_${index}`}
                                            inputType="number"
                                            placeholder="0"
                                            value={item.itemLength || ''}
                                            onChangeFunc={handleNumberChange(index, 'itemLength')}
                                            disabled={isLoading}
                                            required
                                            errorText={itemErrors?.itemLength ? true : ''}
                                            min="0"
                                            inputValueUnitType={lengthUnit}
                                            shouldFormatNumber={true}
                                            maxLength={4}
                                        />
                                    </div>

                                    {/* Width */}
                                    <div className="w-[202px]">
                                        <InputText
                                            label={`${t('Orders.Width')} (${lengthUnit})`}
                                            labelClassName="text-xs font-medium"
                                            inputName={`width_${index}`}
                                            inputType="number"
                                            placeholder="0"
                                            value={item.width || ''}
                                            onChangeFunc={handleNumberChange(index, 'width')}
                                            disabled={isLoading}
                                            required
                                            errorText={typeof itemErrors?.width === 'string' ? itemErrors.width : itemErrors?.width ? true : ''}
                                            isIconError={typeof itemErrors?.width === 'string'}
                                            min="0"
                                            inputValueUnitType={lengthUnit}
                                            shouldFormatNumber={true}
                                            maxLength={4}
                                        />
                                    </div>

                                    {/* Height */}
                                    <div className="w-[202px]">
                                        <InputText
                                            label={`${t('Orders.Height')} (${lengthUnit})`}
                                            labelClassName="text-xs font-medium"
                                            inputName={`height_${index}`}
                                            inputType="number"
                                            placeholder="0"
                                            value={item.height || ''}
                                            onChangeFunc={handleNumberChange(index, 'height')}
                                            disabled={isLoading}
                                            required
                                            errorText={typeof itemErrors?.height === 'string' ? itemErrors.height : itemErrors?.height ? true : ''}
                                            isIconError={typeof itemErrors?.height === 'string'}
                                            min="0"
                                            inputValueUnitType={lengthUnit}
                                            shouldFormatNumber={true}
                                            maxLength={4}
                                        />
                                    </div>

                                    {/* Unit Weight */}
                                    <div className="w-[202px]">
                                        <InputText
                                            label={`${t('Orders.Unit_Weight')} (${weightUnit})`}
                                            labelClassName="text-xs font-medium"
                                            inputName={`weight_${index}`}
                                            inputType="number"
                                            placeholder="0"
                                            value={item.weight || ''}
                                            onChangeFunc={handleNumberChange(index, 'weight')}
                                            disabled={isLoading}
                                            required
                                            errorText={itemErrors?.weight ? true : ''}
                                            min="0"
                                            inputValueUnitType={weightUnit}
                                            shouldFormatNumber={true}
                                            maxLength={7}
                                        />
                                    </div>

                                    {/* Freight Class */}
                                    <div className="w-[202px]">
                                        <InputText
                                            label={t('Orders.Freight_Class')}
                                            labelClassName="text-xs font-medium"
                                            inputName={`freightClass_${index}`}
                                            inputType="number"
                                            placeholder="0"
                                            value={item.freightClass || ''}
                                            disabled={true}
                                            maxLength={3}
                                            required
                                            errorText=""
                                            shouldFormatNumber={true}
                                        />
                                    </div>

                                    {/* Commodity */}
                                    <div className="w-[202px]">
                                        <SelectBox
                                            label={t('Orders.Commodity')}
                                            labelClassName="text-xs font-medium mb-1.5 block"
                                            placeholder={t('Orders.Select_commodity')}
                                            options={commodityOptions || []}
                                            value={selectedCommodity}
                                            onChangeFunc={handleSelectChange(index, 'commodityId')}
                                            isDisabled={isLoading}
                                            isClearable
                                        />
                                    </div>

                                    {/* Toggle Row */}
                                    <div className="w-full flex flex-row items-center py-2 gap-2">
                                        {(() => {
                                            const wm = weightUnit.toUpperCase();
                                            const blocked = isStackingBlocked(currentLegData?.equipmentType, item.height, wm);
                                            const blockedMessage = getStackingBlockedMessage(currentLegData?.equipmentType, item.height, wm);

                                            const toggleEl = (
                                                <Toggle
                                                    name={`isStack_${index}`}
                                                    isChecked={blocked || item.isStack}
                                                    onChange={() => handleLineItemChange(index, 'isStack', !item.isStack)}
                                                    isDisabled={isLoading || blocked}
                                                    switchClassName={blocked ? 'cursor-not-allowed' : 'cursor-pointer'}
                                                    className={blocked ? 'pointer-events-none' : ''}
                                                />
                                            );

                                            return blocked ? (
                                                <TooltipCmp message={blockedMessage} childrenClassName="flex">
                                                    <div className="inline-block">{toggleEl}</div>
                                                </TooltipCmp>
                                            ) : (
                                                toggleEl
                                            );
                                        })()}
                                        <span className="text-xs font-medium">{t('Orders.Do_not_stack')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Visualize Modal */}
            {visualizeIndex !== null && lineItems[visualizeIndex] && (
                <VisualizeUnit
                    weightMeasurement={weightUnit === 'lbs' ? 'LBS' : 'KGS'}
                    lengthMeasurement={weightUnit === 'lbs' ? 'INCHES' : 'CM'}
                    parentFreightClass={lineItems[visualizeIndex].freightClass}
                    handleClose={() => setVisualizeIndex(null)}
                    currentData={{ index: visualizeIndex, data: lineItems[visualizeIndex] }}
                    unitArr={handlingUnitOptions || []}
                    handleVisualizeUnitSubmit={handleVisualizeSubmit}
                    isEnable={!isLoading}
                    primaryEquipmentType={currentLegData?.equipmentType ?? ''}
                    primaryEquipmentTypeFullName={currentLegData?.equipmentTypeFullName ?? ''}
                />
            )}
        </div>
    );
};

export default LineItemsSection;
